/**
 * External Auth Flow Helpers
 *
 * Centralized handling for external authentication flows (mobile apps, CLI tools).
 * These flows store callback URLs in sessionStorage and redirect after auth.
 *
 * Flow:
 * 1. External client opens /login with params (e.g., ?cli=true&cli_callback=...)
 * 2. Login.tsx stores params in sessionStorage (for OAuth flows that navigate away)
 * 3. User authenticates (password, Google, SSO, etc.)
 * 4. After auth, handleExternalAuthRedirect() checks sessionStorage/URL and redirects
 *
 * Special case: Already-authenticated users
 * - If user is already logged in when browser opens /login with params,
 *   Login.tsx never renders (App.tsx routes to dashboard instead)
 * - So we also check URL params directly, not just sessionStorage
 */

export interface ExternalAuthDeps {
    apiHost: string;
    getUrlSearch: () => string;
    getSessionItem: (key: string) => string | null;
    removeSessionItem: (key: string) => void;
    redirect: (url: string) => void;
}

const getApiHostFromWindow = (): string => {
    if (typeof window === 'undefined') {
        throw new Error('Required environment variable is not defined: API_HOST');
    }

    const env = (window as { ENV?: Record<string, string | undefined> }).ENV;
    const apiHost = env?.API_HOST;

    if (apiHost === undefined) {
        throw new Error('Required environment variable is not defined: API_HOST');
    }

    return apiHost;
};

const getDefaultDeps = (): ExternalAuthDeps => {
    const apiHost = getApiHostFromWindow();
    return {
        apiHost,
        getUrlSearch: () => window.location.search,
        getSessionItem: (key) => sessionStorage.getItem(key),
        removeSessionItem: (key) => sessionStorage.removeItem(key),
        redirect: (url) => {
            window.location.href = url;
        },
    };
};

/**
 * Core logic for handling external auth redirects.
 * Exported for testing with injected dependencies.
 */
export const handleExternalAuthRedirectWithDeps = (deps: ExternalAuthDeps): boolean => {
    // Check for CLI callback - first in sessionStorage, then in URL params
    // URL params cover the case where user is already authenticated (Login.tsx never rendered)
    const urlParams = new URLSearchParams(deps.getUrlSearch());
    const cliCallback = deps.getSessionItem('cli_callback') || urlParams.get('cli_callback');

    if (cliCallback) {
        console.log('[CLI Login] Redirecting to CLI callback');
        deps.removeSessionItem('cli_callback');
        const params = new URLSearchParams({ callback: cliCallback });
        deps.redirect(`${deps.apiHost}/api/auth/cli-oauth-callback/?${params.toString()}`);
        return true;
    }

    // Check for mobile callback - first in sessionStorage, then in URL params
    const mobileRedirectUri =
        deps.getSessionItem('mobile_redirect_uri') || urlParams.get('app_redirect_uri');

    if (mobileRedirectUri) {
        console.log('[Mobile Login] Redirecting to mobile callback');
        const appType =
            deps.getSessionItem('mobile_app_type') || urlParams.get('app_type') || 'facility';
        deps.removeSessionItem('mobile_login');
        deps.removeSessionItem('mobile_redirect_uri');
        deps.removeSessionItem('mobile_app_type');
        deps.removeSessionItem('mobile_email');
        const params = new URLSearchParams({
            app_redirect_uri: mobileRedirectUri,
            app_type: appType,
        });
        deps.redirect(`${deps.apiHost}/api/auth/mobile-oauth-callback/?${params.toString()}`);
        return true;
    }

    return false;
};

/**
 * Checks for external auth flows in sessionStorage or URL params and redirects if found.
 * Call this after successful authentication before doing any other redirects.
 *
 * Checks both sessionStorage (for OAuth flows) and current URL (for already-authenticated users).
 *
 * @returns true if an external auth redirect was triggered, false otherwise
 */
export const handleExternalAuthRedirect = (): boolean => {
    return handleExternalAuthRedirectWithDeps(getDefaultDeps());
};
